import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/base/Seo";
import VideoCard from "@/components/feature/VideoCard";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";

function parseViews(v: string) {
  const num = parseFloat(v.replace("万", ""));
  return v.includes("万") ? num * 10000 : num;
}

const sorts = [
  { id: "latest", label: "最新发布" },
  { id: "hot", label: "热度最高" },
];

export default function ColumnPage() {
  const { slug } = useParams();
  const cat = categories.find((c) => c.slug === slug);
  const [sort, setSort] = useState("latest");

  const list = useMemo(() => {
    if (!cat) return [];
    let items = videos.filter((v) => v.categoryId === cat.id);
    if (sort === "hot") items = [...items].sort((a, b) => parseViews(b.views) - parseViews(a.views));
    return items;
  }, [cat, sort]);

  if (!cat) {
    return (
      <Layout>
        <Seo title="栏目不存在" description="未找到对应栏目，请返回首页继续浏览娱乐圈新鲜事。" />
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-error-warning-line text-5xl text-primary-400" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">栏目不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的栏目可能已下线或地址有误。</p>
          <Link to="/" className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://soulhandmade.cn/" },
      { "@type": "ListItem", position: 2, name: cat.name, item: `https://soulhandmade.cn/columns/${cat.slug}` },
    ],
  };

  return (
    <Layout>
      <Seo
        title={`${cat.name} - 最新${cat.name}视频与圈内速递`}
        description={`${cat.desc} 吃瓜视频网${cat.name}栏目实时更新，为你聚合最新热点视频与圈内动态。`}
        keywords={`${cat.name},${cat.name}视频,娱乐八卦,圈内速递`}
        canonical={`https://soulhandmade.cn/columns/${cat.slug}`}
        type="collectionPage"
        jsonLd={breadcrumbJsonLd}
      />

      {/* Category banner */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={cat.image} alt={`${cat.name}栏目背景`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/30"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-background-100/80" aria-label="面包屑">
            <Link to="/" className="hover:text-background-50">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{cat.name}</span>
          </nav>
          <span className="mt-6 inline-flex h-11 w-11 items-center justify-center rounded-full bg-background-50/15 text-accent-300 ring-1 ring-inset ring-background-50/30">
            <i className={`${cat.icon} text-xl`} />
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">{cat.name}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-background-100/90">{cat.desc}</p>
          <p className="mt-4 text-xs font-medium text-accent-300">{cat.count.toLocaleString()} 条速递 · 实时更新</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {cat.name}精选
          </h2>
          <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1 ring-1 ring-inset ring-background-200">
            {sorts.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSort(s.id)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition ${
                  sort === s.id ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>
      </section>

      {/* Other columns */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
        <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950">更多栏目</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {categories.filter((c) => c.id !== cat.id).map((c) => (
            <Link
              key={c.id}
              to={`/columns/${c.slug}`}
              className="group flex items-center gap-4 rounded-xl bg-background-50 p-4 ring-1 ring-inset ring-background-200 transition hover:ring-primary-300"
            >
              <img src={c.image} alt={c.name} className="h-14 w-20 shrink-0 rounded-lg object-cover" />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground-900">{c.name}</h3>
                <p className="mt-0.5 truncate text-xs text-foreground-500">{c.desc}</p>
              </div>
              <i className="ri-arrow-right-line ml-auto text-foreground-300 transition group-hover:text-primary-600" />
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}